import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import TokenManager from './tokenManager';

class SecureApi {
  private api: AxiosInstance;
  private isInitialLoad: boolean = true;
  private retryCount: number = 0;
  private maxRetries: number = 3;

  constructor() {
    this.api = axios.create({
      baseURL: process.env.REACT_APP_API_BASE_URL,
      timeout: 10000,
    });

    // Set initial load to false after a delay
    setTimeout(() => {
      this.isInitialLoad = false;
    }, 2000); // 2 seconds grace period for page refresh

    // Request interceptor
    this.api.interceptors.request.use(
      async (config) => {
        // Check if token needs refresh before making request
        await TokenManager.refreshTokenIfNeeded();
        
        const token = TokenManager.getToken();
        if (token) {
          config.headers.Authorization = token; // No Bearer prefix as per API requirements
        }
        
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor
    this.api.interceptors.response.use(
      (response: AxiosResponse) => {
        // Reset retry count on successful response
        this.retryCount = 0;
        return response;
      },
      async (error) => {
        const originalRequest = error.config;

        // If token is invalid (401) and we haven't already tried to refresh
        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;

          // During initial load, be more tolerant and retry
          if (this.isInitialLoad && this.retryCount < this.maxRetries) {
            this.retryCount++;
            console.log(`Retrying request during initial load (attempt ${this.retryCount}/${this.maxRetries})`);
            
            // Wait a bit before retrying
            await new Promise(resolve => setTimeout(resolve, 500 * this.retryCount));
            
            // Retry the request
            return this.api(originalRequest);
          }

          try {
            // Try to refresh token
            const refreshed = await TokenManager.refreshTokenIfNeeded();
            if (refreshed) {
              // Retry the original request with new token
              const token = TokenManager.getToken();
              if (token) {
                originalRequest.headers.Authorization = token;
                return this.api(originalRequest);
              }
            }
          } catch (refreshError) {
            console.error('Token refresh failed:', refreshError);
          }

          // Only clear token and redirect if not in initial load phase
          if (!this.isInitialLoad) {
            console.log('Authentication failed, clearing token and redirecting to login');
            TokenManager.clearToken();
            TokenManager.clearUserData();
            if (typeof window !== 'undefined') {
              window.location.href = '/login';
            }
          } else {
            console.log('Authentication failed during initial load, not clearing token yet');
          }
        }

        return Promise.reject(error);
      }
    );
  }

  // GET request
  async get<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.api.get<T>(url, config);
    return response.data;
  }

  // POST request
  async post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.api.post<T>(url, data, config);
    return response.data;
  }

  // PUT request
  async put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.api.put<T>(url, data, config);
    return response.data;
  }

  // DELETE request
  async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.api.delete<T>(url, config);
    return response.data;
  }

  // PATCH request
  async patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.api.patch<T>(url, data, config);
    return response.data;
  }
}

export default new SecureApi(); 